import { getPeriodsByType } from '@yodamoda/core/shared/date.functions'
import { type PeriodsState } from '@yodamoda/core/shared/period-state'
import { PeriodType } from '@yodamoda/core/shared/period-type'

const VALID_PERIOD_TYPES = new Set<string>(Object.values(PeriodType))

export function appendPeriodQueryParams(path: string, period: PeriodsState): string {
  const params = new URLSearchParams()
  params.set('period', period.type)
  if (period.type === PeriodType.Custom) {
    params.set('from', String(period.current.from))
    params.set('to', String(period.current.to))
  }
  const query = params.toString()
  return query ? `${path}?${query}` : path
}

function datedPeriod(fromParam: string | undefined, toParam: string | undefined): PeriodsState | undefined {
  if (fromParam === undefined || toParam === undefined) return undefined
  const from = parseInt(fromParam, 10)
  const to = parseInt(toParam, 10)
  if (Number.isNaN(from) || Number.isNaN(to) || to <= from) return undefined
  return { type: PeriodType.Custom, current: { from, to }, prev: { from: from - (to - from), to: from } }
}

/**
 * Reads the URL's period back into state, applying `from`/`to` seconds exactly as written so a round trip cannot drift;
 * a named period stays named only while its rolling range matches, and All Time is refused because it has no bounds.
 */
export function parsePeriodFromSearchParams(
  searchParams: Record<string, string | undefined>
): PeriodsState | undefined {
  const periodParam = searchParams.period
  if (!periodParam || !VALID_PERIOD_TYPES.has(periodParam)) return undefined

  const type = periodParam as PeriodType

  if (type === PeriodType.Custom) return datedPeriod(searchParams.from, searchParams.to)

  if (type === PeriodType.AllTime) return undefined

  const named = { ...getPeriodsByType(type), type }
  const dated = datedPeriod(searchParams.from, searchParams.to)
  if (!dated) return named
  return dated.current.from === named.current.from && dated.current.to === named.current.to ? named : dated
}

/** The custom range the URL's `from`/`to` carry, whatever period it names: what a page opens on when
 * the seller's plan does not offer that name. */
export function parseDatedPeriodFromSearchParams(
  searchParams: Record<string, string | undefined>
): PeriodsState | undefined {
  return datedPeriod(searchParams.from, searchParams.to)
}
